import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import { Icon } from "@iconify/react";
import { useNavigate } from "react-router-dom";
import Logo from "../Logo";
import RegisterModal from "../Auth/RegisterModal";
import styles from "./LoginModal.module.css";
import { useAuth } from "../../contexts/JWTContext";

const REMEMBER_LOGIN_KEY = "landingRememberedLogin";

const emptyRememberedLogin = {
  username: "",
  password: "",
  rememberPassword: false,
};

const saveRememberedLogin = ({ username, password, rememberPassword }) => {
  try {
    if (!rememberPassword) {
      localStorage.removeItem(REMEMBER_LOGIN_KEY);
      return;
    }

    localStorage.setItem(
      REMEMBER_LOGIN_KEY,
      JSON.stringify({
        username,
        password,
        rememberPassword: true,
      }),
    );
  } catch {}
};

const clearRememberedLogin = () => {
  try {
    localStorage.removeItem(REMEMBER_LOGIN_KEY);
  } catch {}
};

const getRememberedLogin = () => {
  try {
    const rawValue = localStorage.getItem(REMEMBER_LOGIN_KEY);
    if (!rawValue) {
      return emptyRememberedLogin;
    }

    const parsedValue = JSON.parse(rawValue);
    return {
      username: parsedValue?.username || "",
      password: parsedValue?.password || "",
      rememberPassword: Boolean(parsedValue?.rememberPassword),
    };
  } catch {
    return emptyRememberedLogin;
  }
};

const LoginModal = memo(({ isOpen, onClose, onOpenRegister, hideClose }) => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const onlineCount = useMemo(() => Math.floor(5000 + Math.random() * 501), []);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState(() => {
    const rememberedLogin = getRememberedLogin();
    return {
      username: rememberedLogin.username,
      password: rememberedLogin.password,
    };
  });
  const [rememberPassword, setRememberPassword] = useState(
    () => getRememberedLogin().rememberPassword,
  );
  const [showRegister, setShowRegister] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  // track if modal was opened via global event
  const [openFromEvent, setOpenFromEvent] = useState(false);

  const closeModal = useCallback(() => {
    const nextRememberedLogin = getRememberedLogin();
    setOpenFromEvent(false);
    setError("");
    setShowPassword(false);
    setRememberPassword(nextRememberedLogin.rememberPassword);
    setFormData({
      username: nextRememberedLogin.rememberPassword
        ? nextRememberedLogin.username
        : "",
      password: nextRememberedLogin.rememberPassword
        ? nextRememberedLogin.password
        : "",
    });
    if (onClose) onClose();
  }, [onClose]);

  const handleOverlayClick = useCallback(
    (e) => {
      if (e.target === e.currentTarget) {
        closeModal();
      }
    },
    [closeModal],
  );

  const handleEscapeKey = useCallback(
    (e) => {
      if (e.key === "Escape") {
        closeModal();
      }
    },
    [closeModal],
  );

  useEffect(() => {
    const onOpenLogin = () => {
      setError("");
      setShowPassword(false);
      const nextRememberedLogin = getRememberedLogin();
      setRememberPassword(nextRememberedLogin.rememberPassword);
      setFormData({
        username: nextRememberedLogin.username,
        password: nextRememberedLogin.password,
      });
      setOpenFromEvent(true);
    };
    window.addEventListener("open-login-modal", onOpenLogin);
    return () => window.removeEventListener("open-login-modal", onOpenLogin);
  }, []);

  const togglePasswordVisibility = useCallback(() => {
    setShowPassword((prev) => !prev);
  }, []);

  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  }, []);

  const handleRememberPasswordChange = useCallback(
    (event) => {
      const checked = event.target.checked;
      setRememberPassword(checked);

      if (!checked) {
        clearRememberedLogin();
        return;
      }

      saveRememberedLogin({
        username: formData.username.trim(),
        password: formData.password,
        rememberPassword: true,
      });
    },
    [formData.password, formData.username],
  );

  useEffect(() => {
    if (!rememberPassword) {
      return;
    }

    saveRememberedLogin({
      username: formData.username.trim(),
      password: formData.password,
      rememberPassword: true,
    });
  }, [formData.password, formData.username, rememberPassword]);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      setError("");
      setLoading(true);
      try {
        await login(formData.username.trim(), formData.password);

        if (rememberPassword) {
          saveRememberedLogin({
            username: formData.username.trim(),
            password: formData.password,
            rememberPassword: true,
          });
        } else {
          clearRememberedLogin();
        }

        setLoading(false);
        setFormData({
          username: rememberPassword ? formData.username.trim() : "",
          password: rememberPassword ? formData.password : "",
        });
        closeModal();
        navigate("/home", { replace: true });
      } catch (err) {
        setLoading(false);
        setError(
          err?.response?.data?.message || err?.message || "Login failed",
        );
      }
    },
    [formData, login, closeModal, navigate, rememberPassword],
  );

  const handleOpenRegister = useCallback(() => {
    if (onOpenRegister) {
      onOpenRegister(); // delegate to parent if provided
    } else {
      setShowRegister(true); // fallback: open internal register modal
    }
    closeModal();
  }, [onOpenRegister, closeModal]);

  useEffect(() => {
    const active = isOpen || openFromEvent || showRegister;
    if (active) {
      document.addEventListener("keydown", handleEscapeKey);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.removeEventListener("keydown", handleEscapeKey);
      document.body.style.overflow = "unset";
    };
  }, [isOpen, openFromEvent, showRegister, handleEscapeKey]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const nextRememberedLogin = getRememberedLogin();
    setRememberPassword(nextRememberedLogin.rememberPassword);
    setFormData({
      username: nextRememberedLogin.username,
      password: nextRememberedLogin.password,
    });
  }, [isOpen]);

  if (!isOpen && !showRegister && !openFromEvent) return null;

  return (
    <>
      {(isOpen || openFromEvent) && (
        <div className={styles.overlay} onClick={handleOverlayClick}>
          <div className={styles.modal}>
            {!hideClose && (
              <Icon
                className={styles.closeButton}
                icon="line-md:close-circle-twotone"
                style={{ color: "red", fontSize: "40px" }}
                onClick={closeModal}
              />
            )}

            <div className={styles.content}>
              <div className={styles.loginHeader}>
                <Logo size="xlarge" />
                <h2 className={styles.loginTitle}>ยินดีต้อนรับ</h2>
              </div>

              <div className={styles.statusBar}>
                <div className={styles.statusLeft}>
                  <span className={styles.statusDot} />
                  สมาชิกออนไลน์
                  <span className={styles.statusCount}>
                    {onlineCount.toLocaleString("en-US")}
                  </span>
                </div>
                <span className={styles.statusBadge}>User</span>
              </div>

              <div className={styles.tabRow}>
                <button
                  type="button"
                  className={`${styles.tabButton} ${styles.tabActive}`}
                >
                  เข้าสู่ระบบ
                </button>
                <button
                  type="button"
                  className={styles.tabButton}
                  onClick={handleOpenRegister}
                >
                  สมัครสมาชิก
                </button>
              </div>

              <form className={styles.form} onSubmit={handleSubmit}>
                <div className={styles.inputGroup}>
                  <div className={styles.inputWrapper}>
                    <Icon
                      icon="material-symbols:person-rounded"
                      className={styles.inputIcon}
                      width={26}
                    />
                    <input
                      type="text"
                      name="username"
                      className={styles.input}
                      placeholder="หมายเลขโทรศัพท์"
                      value={formData.username}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>

                <div className={styles.inputGroup}>
                  <div className={styles.inputWrapper}>
                    <Icon
                      icon="material-symbols:lock-person-rounded"
                      className={styles.inputIcon}
                      width={26}
                    />
                    <input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      className={styles.input}
                      placeholder="รหัสผ่าน"
                      value={formData.password}
                      onChange={handleInputChange}
                      required
                    />
                    <button
                      type="button"
                      className={styles.passwordToggle}
                      onClick={togglePasswordVisibility}
                    >
                      <Icon
                        icon={
                          showPassword
                            ? "material-symbols:visibility-off-rounded"
                            : "material-symbols:visibility-rounded"
                        }
                        width={24}
                      />
                    </button>
                  </div>
                </div>

                <label className={styles.rememberRow}>
                  <input
                    type="checkbox"
                    className={styles.rememberCheckboxHidden}
                    checked={rememberPassword}
                    onChange={handleRememberPasswordChange}
                  />
                  <span className={styles.rememberLeft}>
                    <Icon
                      icon="solar:lock-password-bold-duotone"
                      width={15}
                      className={styles.rememberIcon}
                    />
                    <span className={styles.rememberLabel}>จดจำรหัสผ่าน</span>
                  </span>
                  <span
                    className={`${styles.rememberToggle}${rememberPassword ? ` ${styles.rememberToggleOn}` : ""}`}
                  >
                    <span className={styles.rememberThumb} />
                  </span>
                </label>

                {error && <div className={styles.errorText}>{error}</div>}

                <button
                  type="submit"
                  className={styles.submitButton}
                  disabled={loading}
                >
                  {loading ? "กำลังเข้าสู่ระบบ..." : "เข้าสู่ระบบ"}
                </button>
              </form>
            </div>
          </div>
        </div>
      )}
      {showRegister && (
        <RegisterModal
          onClose={() => {
            setShowRegister(false);
          }}
          onSwitchToLogin={() => {
            setShowRegister(false);
            // reopen login if desired
          }}
        />
      )}
    </>
  );
});

LoginModal.displayName = "LoginModal";

export default LoginModal;
